import * as THREE from 'three';
import { composeEyeLook } from '../character/eyeLook';
import { FaceRig } from '../character/faceRig';
import type { FaceFrame } from '../character/faceRig';

export const DEER_EXPRESSIONS = {
  calm: { eyeSquintLeft: .20, eyeSquintRight: .20, browInnerUp: .08 },
  curious: { browInnerUp: .45, browOuterUpLeft: .8, browOuterUpRight: .35, eyeWideLeft: .22, eyeWideRight: .16 },
  alert: { browInnerUp: .5, browOuterUpLeft: .8, browOuterUpRight: .8, eyeWideLeft: .65, eyeWideRight: .65 },
  worried: { browInnerUp: .95, eyeSadLeft: .85, eyeSadRight: .85 },
  annoyed: { browDownLeft: .95, browDownRight: .95, eyeTenseLeft: .85, eyeTenseRight: .85 },
} satisfies Record<string, FaceFrame>;
export type DeerExpression = keyof typeof DEER_EXPRESSIONS;

/** Optional live gaze layer for the forest-deer asset. Construct in the bind pose
 * and update after AnimationMixer. Without an override, the authored eyes play. */
export class DeerEyes {
  readonly face = new FaceRig();
  private eyes: { bone: THREE.Object3D; rest: THREE.Quaternion; current: THREE.Quaternion; up: THREE.Vector3; right: THREE.Vector3 }[] = [];
  private lids: THREE.Mesh[] = [];
  private target = new THREE.Quaternion();
  private pitch = new THREE.Quaternion();
  private delta = new THREE.Quaternion();
  private gaze: { yaw: number; pitch: number } | null = null;
  private blink: number | undefined;
  private expression: DeerExpression | null = null;

  constructor(root: THREE.Object3D) {
    root.updateMatrixWorld(true);
    for (const name of ['eye_l', 'eye_r']) {
      const bone = root.getObjectByName(name);
      if (!bone) throw new Error(`Missing deer gaze bone: ${name}`);
      const inv = bone.getWorldQuaternion(new THREE.Quaternion()).invert();
      this.eyes.push({ bone, rest: bone.quaternion.clone(), current: bone.quaternion.clone(),
        up: new THREE.Vector3(0, 1, 0).applyQuaternion(inv),
        right: new THREE.Vector3(-1, 0, 0).applyQuaternion(inv) });
    }
    root.traverse(o => {
      if (o instanceof THREE.Mesh && o.morphTargetDictionary &&
          Object.keys(o.morphTargetDictionary).some(n => /^(eye|brow)/.test(n))) this.lids.push(o);
    });
  }

  setGaze(yaw: number, pitch: number, blink?: number): void {
    if (!this.gaze) for (const eye of this.eyes) eye.current.copy(eye.bone.quaternion);
    // Keep a small sclera crescent; larger attention turns come from the neck.
    this.gaze = { yaw: THREE.MathUtils.clamp(yaw, -.12, .12), pitch: THREE.MathUtils.clamp(pitch, -.09, .09) };
    this.blink = blink === undefined ? undefined : THREE.MathUtils.clamp(blink, 0, 1);
    this.syncFace();
  }

  setExpression(expression: DeerExpression | null): void {
    this.expression = expression;
    this.face.setEmote(expression ? DEER_EXPRESSIONS[expression] : null);
    this.syncFace();
  }

  private syncFace(): void {
    // Gaze must not replace the expression channel. Explicit blink controls can
    // override it; otherwise the existing character auto-blink stays active.
    this.face.setCaptured(this.blink === undefined ? null : {
      ...(this.expression ? DEER_EXPRESSIONS[this.expression] : {}),
      eyeBlinkLeft: this.blink, eyeBlinkRight: this.blink,
    });
  }

  clear(): void {
    this.gaze = null; this.blink = undefined; this.face.setCaptured(null);
    for (const eye of this.eyes) eye.bone.quaternion.copy(eye.rest);
    for (const mesh of this.lids) mesh.morphTargetInfluences?.fill(0);
  }

  update(dt: number): void {
    if (!this.gaze && !this.expression) return;
    let blink = 0;
    this.face.update(dt, (name, value) => {
      if (name.startsWith('eyeBlink')) blink = Math.max(blink, value);
      for (const mesh of this.lids) {
        const i = mesh.morphTargetDictionary?.[name];
        if (i !== undefined) mesh.morphTargetInfluences![i] = value;
      }
    });
    // Same blink-dependent centering and saccade response used by the avatars.
    if (!this.gaze) return;
    const duck = 1 - blink * .75;
    for (const eye of this.eyes) {
      composeEyeLook(this.delta, this.pitch, eye.up, eye.right, this.gaze.yaw * duck, this.gaze.pitch * duck);
      this.target.copy(eye.rest).multiply(this.delta);
      eye.current.slerp(this.target, 1 - Math.exp(-28 * dt));
      eye.bone.quaternion.copy(eye.current);
    }
  }
}
